import { useEffect, useState } from "react";
import {
  BlogAnalytics,
  PostAnalytics,
  analyticsBlogs,
  analyticsPosts,
} from "../lib/api";
import Spinner from "../ui/Spinner";
import { IconChart, IconEye, IconSparkles } from "../ui/icons";
import type { ReactNode } from "react";

export default function Analytics() {
  const [blogs, setBlogs] = useState<BlogAnalytics[]>([]);
  const [posts, setPosts] = useState<PostAnalytics[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    Promise.all([analyticsBlogs(), analyticsPosts()])
      .then(([b, p]) => {
        setBlogs(b);
        setPosts(p);
      })
      .finally(() => setLoading(false));
  }, []);

  if (loading) {
    return (
      <div className="flex items-center gap-2 text-ink-500">
        <Spinner size={14} /> Loading analytics…
      </div>
    );
  }

  const totalClicks = blogs.reduce((s, b) => s + b.total_clicks, 0);
  const totalImpr = blogs.reduce((s, b) => s + b.total_impressions, 0);
  const totalEng = blogs.reduce((s, b) => s + b.total_engagement, 0);

  return (
    <div>
      <header className="mb-8">
        <h1 className="font-display text-3xl md:text-4xl font-bold text-ink-800 tracking-tight">
          Analytics
        </h1>
        <p className="text-sm text-ink-500 mt-1.5">Last 30 days</p>
      </header>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-10">
        <StatCard
          label="Total blog clicks"
          value={totalClicks}
          icon={<IconEye size={18} />}
          tone="brand"
        />
        <StatCard
          label="Total impressions"
          value={totalImpr}
          icon={<IconChart size={18} />}
          tone="mint"
        />
        <StatCard
          label="Total engagement"
          value={totalEng}
          icon={<IconSparkles size={18} />}
          tone="ink"
        />
      </div>

      <section className="mb-10">
        <h2 className="font-display text-xl font-semibold text-ink-800 mb-3">
          Blogs
        </h2>
        <TableCard>
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-sand-100 text-ink-600 text-xs uppercase tracking-wide">
                <th className="text-left px-4 py-2.5 font-semibold">Title</th>
                <th className="text-right px-4 py-2.5 font-semibold">Posts</th>
                <th className="text-right px-4 py-2.5 font-semibold">Clicks</th>
                <th className="text-right px-4 py-2.5 font-semibold">Impressions</th>
                <th className="text-right px-4 py-2.5 font-semibold">Engagement</th>
              </tr>
            </thead>
            <tbody>
              {blogs.length === 0 ? (
                <tr>
                  <td colSpan={5} className="text-center py-10 text-ink-400">
                    No data yet — analytics populate after your first published
                    blog.
                  </td>
                </tr>
              ) : (
                blogs.map((b) => (
                  <tr
                    key={b.id}
                    className="border-t border-ink-100 hover:bg-ink-50/60 transition-colors"
                  >
                    <td className="px-4 py-2.5 truncate text-ink-800">{b.title}</td>
                    <td className="text-right px-4 py-2.5 tabular-nums text-ink-700">
                      {b.social_post_count}
                    </td>
                    <td className="text-right px-4 py-2.5 tabular-nums text-ink-800 font-medium">
                      {b.total_clicks}
                    </td>
                    <td className="text-right px-4 py-2.5 tabular-nums text-ink-700">
                      {b.total_impressions}
                    </td>
                    <td className="text-right px-4 py-2.5 tabular-nums text-ink-700">
                      {b.total_engagement}
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </TableCard>
      </section>

      <section>
        <h2 className="font-display text-xl font-semibold text-ink-800 mb-3">
          Top social posts{" "}
          <span className="text-ink-400 text-sm font-normal">
            (by engagement)
          </span>
        </h2>
        <TableCard>
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-sand-100 text-ink-600 text-xs uppercase tracking-wide">
                <th className="text-left px-4 py-2.5 font-semibold">Blog</th>
                <th className="text-left px-4 py-2.5 font-semibold">Angle</th>
                <th className="text-right px-4 py-2.5 font-semibold">FB impr.</th>
                <th className="text-right px-4 py-2.5 font-semibold">IG impr.</th>
                <th className="text-right px-4 py-2.5 font-semibold">Clicks</th>
                <th className="text-right px-4 py-2.5 font-semibold">Engagement</th>
              </tr>
            </thead>
            <tbody>
              {posts.length === 0 ? (
                <tr>
                  <td colSpan={6} className="text-center py-10 text-ink-400">
                    No data yet
                  </td>
                </tr>
              ) : (
                posts.map((p) => (
                  <tr
                    key={p.id}
                    className="border-t border-ink-100 hover:bg-ink-50/60 transition-colors"
                  >
                    <td className="px-4 py-2.5 truncate max-w-xs text-ink-800">
                      {p.blog_title}
                    </td>
                    <td className="px-4 py-2.5 text-ink-700">
                      {p.angle.replace(/_/g, " ")}
                    </td>
                    <td className="text-right px-4 py-2.5 tabular-nums text-ink-700">
                      {p.platform_fb_impressions}
                    </td>
                    <td className="text-right px-4 py-2.5 tabular-nums text-ink-700">
                      {p.platform_ig_impressions}
                    </td>
                    <td className="text-right px-4 py-2.5 tabular-nums text-ink-800 font-medium">
                      {p.total_clicks}
                    </td>
                    <td className="text-right px-4 py-2.5 tabular-nums text-ink-700">
                      {p.engagement}
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </TableCard>
      </section>
    </div>
  );
}

function StatCard({
  label,
  value,
  icon,
  tone,
}: {
  label: string;
  value: number;
  icon: ReactNode;
  tone: "brand" | "mint" | "ink";
}) {
  const toneClass = {
    brand: "bg-brand-50 text-brand-700",
    mint: "bg-mint-100 text-mint-600",
    ink: "bg-ink-100 text-ink-700",
  }[tone];
  return (
    <div className="bg-white border border-ink-100 rounded-xl p-5 shadow-sm shadow-ink-900/5">
      <div className="flex items-start justify-between gap-2 mb-3">
        <p className="text-xs uppercase tracking-wide font-semibold text-ink-500">
          {label}
        </p>
        <div className={`h-8 w-8 rounded-full flex items-center justify-center ${toneClass}`}>
          {icon}
        </div>
      </div>
      <p className="font-display text-3xl font-bold text-ink-800 tabular-nums">
        {value.toLocaleString()}
      </p>
    </div>
  );
}

function TableCard({ children }: { children: ReactNode }) {
  return (
    <div className="bg-white border border-ink-100 rounded-xl overflow-hidden shadow-sm shadow-ink-900/5">
      <div className="overflow-x-auto">{children}</div>
    </div>
  );
}
